import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  Configuration,
  RegistryDetails,
  Section,
  SectionTypes,
} from "../../../resources/modelTypes";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { DisplayPhoto } from "../DisplayPhoto";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import RegistryDefaultImage from "../assets/registryDefaultSmall.webp";
import PostGivingMessageModal from "./PostGivingMessageModal";
import { RegistryDetailModal } from "./RegistryDetailModal";
import { css } from "@emotion/react";
import { theme, styleUtils } from "@minted/minted-components";
import React, { memo, useCallback, useState } from "react";

const cardStyles = {
  card: (isMobile: boolean) => css`
    background-color: ${theme.colors.gray100};
    border: ${styleUtils.rem(1)} solid ${theme.colors.border};
    border-radius: ${styleUtils.rem(8)};
    color: ${theme.colors.textPrimary};
    ${isMobile && `flex: 0 0 calc(50% - ${theme.spacing.x2});`}
    min-height: ${isMobile ? styleUtils.rem(208) : styleUtils.rem(332)};
    width: ${isMobile ? "100%" : styleUtils.rem(256)};
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    cursor: pointer;
    overflow: hidden;
    width: 100%;
    border-top-left-radius: ${styleUtils.rem(8)};
    border-top-right-radius: ${styleUtils.rem(8)};
  `,
  imageInner: css`
    height: 100%;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  noImagePlaceholder: css`
    align-items: center;
    background-color: ${theme.colors.white};
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  `,
  noImageTitle: css`
    ${theme.typeStyles.displayExtraExtraSmallStyles};
    font-style: italic;
    text-align: center;
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.base};
    padding: ${theme.spacing.x4};
    text-align: left;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      padding: ${theme.spacing.x2};
      padding-bottom: ${theme.spacing.x3};
    `)};
  `,
  subtitle: css`
    ${theme.typeStyles.bodyExtraSmallStyles};
    display: flex;
    color: ${theme.colors.textSecondary};
  `,
  title: css`
    display: flex;
    overflow-wrap: break-word;
    word-break: break-word;
    ${theme.typeStyles.titleSmallStyles};
  `,
};

export type RegistryCardProps = {
  configuration: Configuration;
  details: RegistryDetails;
  section: Section;
  subtitle?: string;
};

const RegistryCard = ({
  configuration,
  details,
  section,
  subtitle: subtitleProp,
}: RegistryCardProps) => {
  const { isDemo } = useIsPreviewOrDemo();
  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  const [isRegistryDetailModalOpen, setIsRegistryDetailModalOpen] =
    useState(false);
  const [isPostGivingMessageModalOpen, setIsPostGivingMessageModalOpen] =
    useState(false);

  const subtitle =
    subtitleProp ??
    (section.sectionType === SectionTypes.CashRegistry
      ? "Cash fund"
      : "Registry");

  const openModal = useCallback(() => {
    if (isDemo) {
      return;
    }
    setIsRegistryDetailModalOpen(true);
  }, [isDemo]);

  const closeModal = useCallback(() => {
    setIsRegistryDetailModalOpen(false);
  }, []);

  const openPostGivingMessageModal = useCallback(() => {
    closeModal();
    setIsPostGivingMessageModalOpen(true);
  }, [closeModal]);

  const handleContributeClick = useCallback(() => {
    openPostGivingMessageModal();
  }, [openPostGivingMessageModal]);

  const closePostGivingMessageModal = useCallback(() => {
    setIsPostGivingMessageModalOpen(false);
  }, []);

  const showNoImagePlaceholder =
    details.hideDefaultRegistryImage && !section.image && !section.photo;

  const content = (
    <>
      <div
        css={cardStyles.imageWrapper}
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div css={cardStyles.imageInner}>
          {showNoImagePlaceholder ? (
            <div css={cardStyles.noImagePlaceholder}>
              <span css={cardStyles.noImageTitle}>{details.title}</span>
            </div>
          ) : (
            <DisplayPhoto
              configuration={configuration}
              isRegistryPhoto
              section={section}
              defaultImage={
                section.sectionType === SectionTypes.Registry
                  ? RegistryDefaultImage
                  : CashRegistryDefaultImage
              }
            />
          )}
        </div>
      </div>

      <div
        css={cardStyles.textBlock}
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {subtitle && <div css={cardStyles.subtitle}>{subtitle}</div>}
        {details.title && <div css={cardStyles.title}>{details.title}</div>}
      </div>
    </>
  );

  return (
    <>
      <div css={[cardStyles.card(isMobile), themeTypographyResetStyles]}>
        {content}
      </div>

      {isRegistryDetailModalOpen && (
        <RegistryDetailModal
          configuration={configuration}
          details={details}
          onClose={closeModal}
          open={isRegistryDetailModalOpen}
          section={section}
          onContributeClick={handleContributeClick}
        />
      )}

      {isPostGivingMessageModalOpen && (
        <PostGivingMessageModal
          open={isPostGivingMessageModalOpen}
          onSkipClick={closePostGivingMessageModal}
        />
      )}
    </>
  );
};

const MemoizedRegistryCard = memo(RegistryCard);

export { MemoizedRegistryCard as RegistryCard };
